import React, { Fragment, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { exportCSV, exportExcel } from "../reporting/reportingUtils";

const money = (value) => value == null
  ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(value) || 0);
const name = (row) => row?.identity?.companyName || row?.identity?.externalId || row?.identity?.website || "Unidentified";
const idLabel = (row) => row?.identity?.externalId || row?.identity?.registrationNumber || row?.identity?.website || "—";
const decisionLabel = (value) => String(value || "Unscored").replaceAll("_", " ");
const decisionTone = (value) => ({ approve: "success", review: "warning", decline: "error", insufficient_data: "default" }[value] || "default");
const riskTone = (value) => ({ low: "success", medium: "warning", high: "error" }[String(value || "").toLowerCase()] || "default");

const columns = [
  ["company", "Company"], ["applications", "Applications"], ["match", "Match"], ["score", "Score"],
  ["decision", "Decision"], ["risk", "Risk"], ["evidence", "Evidence"], ["requested", "Requested"], ["recommended", "Recommended"],
];

const sortValue = (row, key) => ({
  company: name(row).toLowerCase(),
  applications: Number(row?.applicationCount || 0),
  match: Number(row?.match?.confidence || 0),
  score: Number(row?.assessment?.score ?? -1),
  decision: String(row?.assessment?.decision || ""),
  risk: ({ high: 3, medium: 2, low: 1 }[String(row?.assessment?.recommendation?.riskBand || "").toLowerCase()] || 0),
  evidence: Number(row?.assessment?.coverage || 0),
  requested: Number(row?.requestedAmount ?? -1),
  recommended: Number(row?.assessment?.recommendation?.amount ?? -1),
}[key]);

const exportHeaders = [
  { key: "company", label: "Company" },
  { key: "merchantId", label: "Merchant ID" },
  { key: "registrationNumber", label: "Registration Number" },
  { key: "website", label: "Website" },
  { key: "applications", label: "Applications" },
  { key: "match", label: "Match (%)" },
  { key: "score", label: "Score" },
  { key: "decision", label: "Decision" },
  { key: "risk", label: "Risk Band" },
  { key: "evidence", label: "Evidence Coverage (%)" },
  { key: "requested", label: "Requested Amount (USD)" },
  { key: "recommended", label: "Recommended Amount (USD)" },
  { key: "term", label: "Term (Months)" },
  { key: "factor", label: "Factor Rate" },
  { key: "guardrails", label: "Policy Guardrails" },
  { key: "nextSteps", label: "Recommended Next Steps" },
];

const exportRow = (row) => ({
  company: name(row),
  merchantId: row?.identity?.externalId || "",
  registrationNumber: row?.identity?.registrationNumber || "",
  website: row?.identity?.website || "",
  applications: Number(row?.applicationCount || 0),
  match: Math.round(Number(row?.match?.confidence || 0) * 100),
  score: row?.assessment?.score ?? "",
  decision: decisionLabel(row?.assessment?.decision),
  risk: row?.assessment?.recommendation?.riskBand || "",
  evidence: Number(row?.assessment?.coverage || 0),
  requested: row?.requestedAmount ?? "",
  recommended: row?.assessment?.recommendation?.amount ?? "",
  term: row?.assessment?.recommendation?.termMonths ?? "",
  factor: row?.assessment?.recommendation?.factorRate ?? "",
  guardrails: (row?.assessment?.guardrailHits || []).map((item) => String(item).replaceAll("_", " ")).join("; "),
  nextSteps: nextSteps(row).join("; "),
});

function nextSteps(row) {
  const decision = row?.assessment?.decision;
  if (decision === "approve") return ["Confirm the recommended offer and commercial terms", "Complete final compliance checks", "Send the offer and monitor acceptance"];
  if (decision === "decline") return ["Review the decline drivers and guardrails", "Confirm the applicable credit-policy rule", "Record an override rationale if escalated"];
  if (decision === "insufficient_data") return ["Collect missing identity and operating evidence", "Add payment, dispute, and performance history", "Re-run the assessment when evidence is complete"];
  return ["Review weak or missing evidence", "Validate internal payment-platform signals", "Approve, resize, or decline with a recorded rationale"];
}

function RecommendationPreview({ open, row, onOpenCompany }) {
  const assessment = row?.assessment || {};
  const recommendation = assessment.recommendation || {};
  const guardrails = assessment.guardrailHits || [];
  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ m: 1.5, p: 2, border: "1px solid #dfe7f2", borderRadius: 2.5, bgcolor: "#f8faff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em" }}>Recommendation summary</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .75 }}>
              <Chip size="small" color={decisionTone(assessment.decision)} label={decisionLabel(assessment.decision)} sx={{ textTransform: "capitalize", fontWeight: 800 }} />
              <Typography sx={{ color: "#17233f", fontWeight: 800 }}>{money(recommendation.amount)} recommended</Typography>
              {recommendation.termMonths && <Chip size="small" variant="outlined" label={`${recommendation.termMonths} months`} />}
              {recommendation.factorRate && <Chip size="small" variant="outlined" label={`Factor ${recommendation.factorRate}`} />}
            </Stack>
          </Box>
          <Button size="small" variant="contained" endIcon={<OpenInNewRoundedIcon />} onClick={(event) => { event.stopPropagation(); onOpenCompany(row.entityId); }}>
            Open full report
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" }, gap: 2, mt: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Decision drivers</Typography>
            <Typography sx={{ mt: .5, color: "#334155", fontSize: 13.5 }}>
              Score {assessment.score ?? "—"} · {assessment.coverage || 0}% evidence · {recommendation.riskBand || "unscored"} risk
            </Typography>
            <Stack direction="row" spacing={.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {(guardrails.length ? guardrails : ["No policy guardrails triggered"]).map((item) => (
                <Chip key={item} size="small" variant="outlined" color={guardrails.length ? "warning" : "success"} label={String(item).replaceAll("_", " ")} sx={{ textTransform: "capitalize" }} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Next steps</Typography>
            <Stack spacing={.55} sx={{ mt: .5 }}>
              {nextSteps(row).map((step, index) => <Typography key={step} sx={{ color: "#334155", fontSize: 13.5 }}><strong>{index + 1}.</strong> {step}</Typography>)}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
}

export default function PaymentPlatformExecutiveTable({ rows = [], onOpenCompany, viewport = "portfolio-assessments", emptyText = "No companies match the current search and filters." }) {
  const [sort, setSort] = useState({ key: "score", direction: "desc" });
  const [expandedId, setExpandedId] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const sortedRows = useMemo(() => [...rows].sort((left, right) => {
    const a = sortValue(left, sort.key);
    const b = sortValue(right, sort.key);
    const result = typeof a === "string" ? a.localeCompare(b) : a - b;
    return sort.direction === "asc" ? result : -result;
  }), [rows, sort]);
  const changeSort = (key) => setSort((current) => ({ key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" }));
  const exportRows = useMemo(() => sortedRows.map(exportRow), [sortedRows]);
  const exportFilename = `crediarc-${viewport}-${new Date().toISOString().slice(0, 10)}`;
  const runExport = (format) => {
    setExportAnchor(null);
    if (format === "csv") exportCSV(exportRows, exportHeaders, exportFilename);
    else exportExcel(exportRows, exportHeaders, exportFilename, "MCA Assessments");
  };

  return (
    <>
      <Box sx={{ px: 2.75, py: 1, bgcolor: "#f8faff", borderTop: "1px solid #edf0f5", borderBottom: "1px solid #edf0f5", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Chip size="small" color="primary" variant="outlined" label="Executive view" sx={{ fontWeight: 800 }} />
        <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>Click a company to preview its recommendation and next steps.</Typography>
        <Typography sx={{ color: "#8a96a8", fontSize: 12, ml: { md: "auto" } }}>Sorted by {columns.find(([key]) => key === sort.key)?.[1]} {sort.direction === "asc" ? "↑" : "↓"} · current page</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          disabled={!sortedRows.length}
          onClick={(event) => setExportAnchor(event.currentTarget)}
          sx={{ ml: .5, borderColor: "#d4deec", color: "#334155", borderRadius: "8px", textTransform: "none", fontWeight: 750 }}
        >
          Export
        </Button>
        <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
          <ListSubheader sx={{ lineHeight: "30px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "#64748b" }}>
            Current page · {sortedRows.length} rows
          </ListSubheader>
          <MenuItem onClick={() => runExport("xlsx")}>Excel workbook (.xlsx)</MenuItem>
          <MenuItem onClick={() => runExport("csv")}>CSV data (.csv)</MenuItem>
        </Menu>
      </Box>
      <TableContainer data-mca-table-viewport={viewport} sx={{ maxHeight: { xs: 340, md: 390 }, overflow: "auto", scrollbarGutter: "stable" }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1120 }}>
          <TableHead><TableRow>{columns.map(([key, label]) => (
            <TableCell key={key} sortDirection={sort.key === key ? sort.direction : false} sx={{ py: 1.25, color: "#64748b", bgcolor: "#f7f9fc", borderColor: "#e9edf4", fontSize: 11, letterSpacing: ".035em", textTransform: "uppercase", fontWeight: 800 }}>
              <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.direction : "asc"} onClick={() => changeSort(key)}>{label}</TableSortLabel>
            </TableCell>
          ))}</TableRow></TableHead>
          <TableBody>
            {sortedRows.map((row) => {
              const expanded = expandedId === row.entityId;
              const risk = row?.assessment?.recommendation?.riskBand;
              return <Fragment key={row.entityId}>
                <TableRow hover aria-expanded={expanded} sx={{ cursor: "pointer", "& td": { py: 1.2, borderColor: expanded ? "transparent" : "#edf0f5" }, "&:hover": { bgcolor: "#f8faff !important" } }} onClick={() => setExpandedId(expanded ? null : row.entityId)}>
                  <TableCell><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box>{expanded ? <KeyboardArrowUpRoundedIcon color="primary" /> : <KeyboardArrowDownRoundedIcon sx={{ color: "#718096" }} />}</Box><Box><Typography sx={{ color: "#2469d8", fontWeight: 800, fontSize: 13.5 }}>{name(row)}</Typography><Typography variant="caption" color="text.secondary">{idLabel(row)}</Typography></Box></Box></TableCell>
                  <TableCell>{Number(row.applicationCount || 0).toLocaleString()}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={`${Math.round(Number(row?.match?.confidence || 0) * 100)}%`} /></TableCell>
                  <TableCell><strong>{row?.assessment?.score ?? "—"}</strong></TableCell>
                  <TableCell><Chip size="small" color={decisionTone(row?.assessment?.decision)} label={decisionLabel(row?.assessment?.decision)} sx={{ textTransform: "capitalize", fontWeight: 800 }} /></TableCell>
                  <TableCell><Chip size="small" color={riskTone(risk)} variant="outlined" label={risk || "—"} sx={{ textTransform: "capitalize", fontWeight: 700 }} /></TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={`${row?.assessment?.coverage || 0}%`} /></TableCell>
                  <TableCell>{money(row.requestedAmount)}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>{money(row?.assessment?.recommendation?.amount)}</TableCell>
                </TableRow>
                <TableRow><TableCell colSpan={9} sx={{ p: 0, borderColor: expanded ? "#dfe7f2" : "transparent", bgcolor: "#fff" }}><RecommendationPreview open={expanded} row={row} onOpenCompany={onOpenCompany} /></TableCell></TableRow>
              </Fragment>;
            })}
            {!sortedRows.length && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>{emptyText}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
